import React, { useState } from 'react';

import { ContentCard, PagedIndexGrid } from '@rockerbox/styleguide';
import { Button, Menu, MenuMenu, MenuItem } from 'semantic-ui-react';

import { useUsersData, useTableColumns, useRolesTableColumns } from './hooks';
import InviteUsersForm from './InviteUsersForm';
import NewRoleButton from './NewRoleButton';
import { RBAC_ASSIGN_ROLES, RBAC_CREATE_CUSTOM_ROLE } from '../../constants/rbac';
import { useGlobalState } from '../../hooks/global';
import { useRBACUsers, useRBACAdvertiser, useRBACRoles, useRBACPermissions } from '../../hooks/useRBAC';

const InviteUsers = ({ hideTitle, showFooter = false }) => {
  const { user, account } = useGlobalState();
  const { loadingUsers, users, handleManageUpdate } = useUsersData();
  const { advertiserExistsRBAC } = useRBACAdvertiser();
  const { roleOptions, rolesData, refreshRoles } = useRBACRoles();
  const { usersWithRoles, userToRoleMap, setUserToRoleMap, refreshUsersToRole } = useRBACUsers(users);
  const { authorizedPermissions } = useRBACPermissions();
  const authorizedToAssignRoles = authorizedPermissions.includes(RBAC_ASSIGN_ROLES);
  const authorizedToCreateCustomRoles = authorizedPermissions.includes(RBAC_CREATE_CUSTOM_ROLE);
  const customRoleExists = !!rolesData.find(({ type }) => type === 'custom');

  const { cols } = useTableColumns({
    user, advertiser: account.pixel_source_name, handleManageUpdate,
    roleOptions, advertiserExistsRBAC, userToRoleMap, setUserToRoleMap,
    authorizedToAssignRoles,
  });

  const { cols: rolesCols } = useRolesTableColumns(authorizedToCreateCustomRoles, refreshRoles, refreshUsersToRole);

  const [showInviteForm, setShowInviteForm] = useState(false);
  const [activeMenuItem, setActiveMenuItem] = useState('Teammates');
  const allMenuItems = ['Teammates', 'Invited', 'Roles'];
  const menuItems = advertiserExistsRBAC ? allMenuItems : allMenuItems.filter(item => item !== 'Roles');
  const data = activeMenuItem === 'Teammates' ? usersWithRoles.filter(row => !row.has_invite) : usersWithRoles.filter(row => row.has_invite);

  return (
    <ContentCard title={!hideTitle && 'Invite Users'} loading={loadingUsers}>
      {showInviteForm && <InviteUsersForm {...{ handleManageUpdate }} />}
      <Menu pointing secondary>
        {menuItems.map(item => (
          <MenuItem
            key={item}
            name={item}
            active={activeMenuItem === item}
            onClick={(e, { name }) => setActiveMenuItem(name)}
          />
        ))}
        <MenuMenu position="right">
          <MenuItem>
            {activeMenuItem === 'Roles'
              ? <NewRoleButton authorized={authorizedToCreateCustomRoles} customRoleExists={customRoleExists} onSaveSuccess={{ refreshRoles, refreshUsersToRole }} />
              : <Button fluid primary content="New User" onClick={() => setShowInviteForm(true)} disabled={!authorizedToAssignRoles} />}
          </MenuItem>
        </MenuMenu>
      </Menu>
      {
        !loadingUsers && activeMenuItem !== 'Roles'
        && (
          <PagedIndexGrid
            className="invite-users-table"
            data={data}
            cols={cols}
            itemsPerPage={10}
            collapsed
            showFooter={showFooter}
          />
        )
      }
      {
        activeMenuItem === 'Roles'
        && (
          <PagedIndexGrid
            className="invite-users-table"
            data={rolesData}
            cols={rolesCols}
            itemsPerPage={10}
            collapsed
            showFooter={showFooter}
          />
        )
      }
    </ContentCard>
  );
};

export default InviteUsers;
